import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';
import { Card } from '../components/ui/card';

const Section = ({ icon: Icon, title, children, variant = 'default' }) => {
  const colors = {
    default: 'bg-primary/10 text-primary',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
    success: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    error: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="mb-8"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[variant]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">{title}</h2>
      </div>
      <div className="text-muted-foreground space-y-4">{children}</div>
    </motion.div>
  );
};

export default function Disclaimer() {
  return (
    <>
      <Helmet>
        <title>Disclaimer - VisualMathLab</title>
        <meta name="description" content="VisualMathLab disclaimer. Important information about the use of our calculators and educational tools." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://visualmathlab.online/disclaimer" />
      </Helmet>

      <div className="min-h-screen py-20 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Disclaimer
            </h1>
            <p className="text-muted-foreground">
              Last Updated: January 2025
            </p>
          </motion.div>

          <Card className="p-8 md:p-12">
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
            >
              <p className="text-foreground font-medium leading-relaxed">
                Please read this disclaimer carefully before using VisualMathLab's calculators and services. 
                By using our website, you acknowledge and agree to the terms outlined below.
              </p>
            </motion.div>

            {/* Educational Purpose */}
            <Section icon={Info} title="Educational Purpose Only">
              <p>
                VisualMathLab is designed as an <strong>educational tool</strong> to help students and 
                learners understand mathematical concepts through interactive visualizations and step-by-step 
                solutions. Our calculators are intended to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Supplement traditional learning methods</li>
                <li>Provide visual representations of mathematical concepts</li>
                <li>Assist in understanding problem-solving approaches</li>
                <li>Verify manual calculations during the learning process</li>
              </ul>
            </Section>

            {/* No Guarantee of Accuracy */}
            <Section icon={XCircle} title="No Guarantee of Accuracy" variant="error">
              <p>
                While we strive to ensure the accuracy of all calculations and information provided on 
                VisualMathLab, we <strong>do not guarantee</strong> that:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>All calculations are 100% accurate or error-free</li>
                <li>The results are suitable for all purposes</li>
                <li>The information is complete or up-to-date</li>
                <li>The service will be available without interruption</li>
              </ul>
              <p className="mt-4 font-medium">
                <strong>Always verify results independently</strong>, especially for critical applications 
                such as exams, professional work, engineering calculations, or financial decisions.
              </p>
            </Section>

            {/* User Responsibility */}
            <Section icon={AlertTriangle} title="User Responsibility" variant="warning">
              <p>
                As a user of VisualMathLab, you acknowledge that:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You are responsible for verifying the accuracy of all results</li>
                <li>You should not rely solely on our calculators for important decisions</li>
                <li>You understand the mathematical principles behind the calculations</li>
                <li>You accept full responsibility for how you use the results</li>
                <li>You will not hold VisualMathLab liable for any errors or omissions</li>
              </ul>
            </Section>

            {/* Not Professional Advice */}
            <Section icon={Info} title="Not Professional Advice">
              <p>
                The information and calculations provided by VisualMathLab do not constitute:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Professional mathematical or scientific advice</li>
                <li>Engineering or technical consultation</li>
                <li>Financial, legal, or medical advice</li>
                <li>Certified or guaranteed results for professional use</li>
              </ul>
              <p className="mt-4">
                For professional or critical applications, please consult with qualified professionals 
                in the relevant field.
              </p>
            </Section>

            {/* Limitation of Liability */}
            <Section icon={XCircle} title="Limitation of Liability" variant="error">
              <p>
                VisualMathLab and its operators, contributors, and affiliates shall not be held liable for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Any errors, inaccuracies, or omissions in calculations or content</li>
                <li>Any losses, damages, or injuries resulting from the use of our service</li>
                <li>Incorrect results used in exams, assignments, or professional work</li>
                <li>Technical issues, downtime, or data loss</li>
                <li>Any decisions made based on our calculations</li>
                <li>Third-party content or links</li>
              </ul>
              <p className="mt-4 font-medium">
                USE THIS SERVICE AT YOUR OWN RISK. We provide the tools "as is" without any warranties.
              </p>
            </Section>

            {/* Numerical Limitations */}
            <Section icon={Info} title="Numerical Limitations">
              <p>
                Our calculators are subject to computational limitations:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Floating-point arithmetic may introduce small rounding errors</li>
                <li>Very large or very small numbers may lose precision</li>
                <li>Complex calculations may have computational limits</li>
                <li>Results may vary slightly between different browsers or devices</li>
              </ul>
            </Section>

            {/* Best Practices */}
            <Section icon={CheckCircle} title="Recommended Best Practices" variant="success">
              <p>
                To get the most value from VisualMathLab while minimizing risks:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Verify Results:</strong> Always double-check important calculations</li>
                <li><strong>Understand Concepts:</strong> Use calculators to learn, not just to get answers</li>
                <li><strong>Cross-Reference:</strong> Compare results with other reliable sources</li>
                <li><strong>Know Limitations:</strong> Understand when to seek professional help</li>
                <li><strong>Report Issues:</strong> Help us improve by reporting any errors you find</li>
              </ul>
            </Section>

            {/* Third-Party Content */}
            <Section icon={Info} title="Third-Party Content and Links">
              <p>
                Our website may contain links to external websites or reference third-party resources. 
                We are not responsible for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>The accuracy or reliability of third-party content</li>
                <li>Privacy practices of external websites</li>
                <li>Availability or functionality of linked resources</li>
              </ul>
            </Section>

            {/* Changes to Service */}
            <Section icon={AlertTriangle} title="Service Changes and Availability" variant="warning">
              <p>
                VisualMathLab reserves the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Modify, update, or discontinue any calculator or feature</li>
                <li>Change formulas or calculation methods to improve accuracy</li>
                <li>Temporarily suspend service for maintenance</li>
                <li>Update this disclaimer at any time</li>
              </ul>
              <p className="mt-4">
                We are not obligated to maintain any specific feature or level of service.
              </p>
            </Section>

            {/* Academic Integrity */}
            <Section icon={Info} title="Academic Integrity">
              <p>
                If you are a student using VisualMathLab:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Ensure your use complies with your institution's academic integrity policies</li>
                <li>Use our tools to understand concepts, not to avoid learning</li>
                <li>Cite our calculators appropriately if required by your instructor</li>
                <li>Check with your teacher about acceptable calculator use during assignments</li>
              </ul>
              <p className="mt-4 font-medium">
                We encourage learning and understanding over simply obtaining answers.
              </p>
            </Section>

            {/* Acceptance */}
            <Section icon={CheckCircle} title="Acceptance of Disclaimer" variant="success">
              <p>
                By using VisualMathLab, you acknowledge that you have read, understood, and agree to 
                this disclaimer. If you do not agree with any part of this disclaimer, please do not 
                use our service.
              </p>
            </Section>

            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mt-12 p-6 bg-muted/50 rounded-xl"
            >
              <h3 className="text-lg font-semibold text-foreground mb-2">Questions or Concerns?</h3>
              <p className="text-muted-foreground mb-4">
                If you have questions about this disclaimer or found an error in our calculations:
              </p>
              <div className="space-y-2">
                <p className="text-sm">
                  <strong>Email:</strong> <a href="mailto:support@visualmathlab.online" className="text-primary hover:underline">support@visualmathlab.online</a>
                </p>
                <p className="text-sm">
                  <strong>Website:</strong> <a href="https://visualmathlab.online" className="text-primary hover:underline">https://visualmathlab.online</a>
                </p>
              </div>
            </motion.div>
          </Card>
        </div>
      </div>
    </>
  );
}